import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Form } from 'antd-mobile';
import { FormInstance } from 'rc-field-form';
import { useTranslation } from 'react-i18next';
import { PickerDate } from 'antd-mobile/es/components/date-picker/util';
import { PickerValue } from 'antd-mobile/es/components/picker-view';

import { GtmEventEnum, sendGtmEvent } from '../../../utils/send-gtm-event';
import { Paths } from '../../../routes/paths-enum';
import { useAppDispatch, useAppSelector } from '../../../hooks/use-redux-hook';
import {
  applicationIdSelector,
  deviceNativeInfoSelector,
  setIsOpenGeoModal,
  setPopupAlert,
  setLoanReviewInputs,
} from '../../../redux/modules/app';
import { usePostApplicationConfirmMutation } from '../../../redux/services/application';
import { useGeo } from '../../../hooks/use-geo';
import { AGREEMENTS_LOAN } from '../../../constants/agreements';
import { ApplicationConfirmDataType } from '../../../types/application';
import { getItemWithExpiry } from '../../../utils/local-storage-with-expiry';
import { LOCAL_STORAGE } from '../../../constants/local-storage';
import { Statuses } from '../../../types/general';
import { REGEXP } from '../../../constants/regexp';
import { IdFormData } from '../../../components/id-form/types';

export const REVIEW_INPUT_NAMES = {
  rfc: 'rfc',
  occupation: 'occupation',
  email: 'email',
};

export const useReviewDataHelper = ({
  form,
  rfcInitial = '',
}: {
  form: FormInstance;
  rfcInitial?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { rfc, occupation, email } = REVIEW_INPUT_NAMES;
  const onClickBack = () => {
    sendGtmEvent(GtmEventEnum.LoanApplication_crossButtonPressed);
    navigate(Paths.HOME);
  };

  const [showOccupation, setShowOccupation] = useState(false);

  const applicationId = useAppSelector(applicationIdSelector);
  const dispatch = useAppDispatch();
  const deviceInfo = useAppSelector(deviceNativeInfoSelector);
  const { deviceId, deviceModel, osVersion, platform } = deviceInfo;

  const occupationCurrent = Form.useWatch(occupation, form);
  const rfcCurrent = Form.useWatch(rfc, form);
  const emailCurrent = Form.useWatch(email, form);
  const RfcPlaceholder = () =>
    rfcCurrent && rfcCurrent.length > 9 ? (
      <>
        <span style={{ color: '#3f7b59' }}>{rfcCurrent}</span>
        <span style={{ color: '#CCCCCC' }}>{'X'.repeat(13 - rfcCurrent.length)}</span>
      </>
    ) : (
      <span style={{ color: '#3f7b59' }}>rfcCurrent</span>
    );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [postApplicationConfirm, { data: confirmData, isLoading, isSuccess, reset }] =
    usePostApplicationConfirmMutation();

  const { handleGetLocation, isLoading: isLoadingGeo } = useGeo();

  const onSuccessGeo = (geo: GeolocationPosition) => {
    sendGtmEvent(GtmEventEnum.LoanApplication_geolocationReceived);
    const formData = {
      agreements: AGREEMENTS_LOAN,
      deviceDetails: {
        geolocation: {
          latitude: geo.coords.latitude,
          longitude: geo.coords.longitude,
        },
      },
      ...form.getFieldsValue(),
    } as ApplicationConfirmDataType;

    const utmSource = getItemWithExpiry(LOCAL_STORAGE.utm_source);
    const clickId = localStorage.getItem(LOCAL_STORAGE.clickid);
    const wmId = localStorage.getItem(LOCAL_STORAGE.wm_id);

    if (utmSource) formData.analyticsTags = { ...formData.analyticsTags, utmSource };
    if (clickId) formData.analyticsTags = { ...formData.analyticsTags, clickId };
    if (wmId) formData.analyticsTags = { ...formData.analyticsTags, wmId };

    if (Object.keys(deviceInfo).length > 0)
      formData.deviceDetails = {
        ...formData.deviceDetails,
        deviceId,
        platform,
        deviceModel,
        osVersion,
      };
    postApplicationConfirm({ data: formData, applicationId });
  };

  const onErrorGeo = (err: GeolocationPositionError) => {
    sendGtmEvent(GtmEventEnum.LoanApplication_geolocationFailed);
    const errorText = t(`yourApplication.geolocationError${err?.code || ''}`);

    dispatch(setPopupAlert({ type: Statuses.ERROR, message: errorText, time: 3000 }));
    dispatch(setIsOpenGeoModal(true));
  };
  const onNavigatorError = () => {
    dispatch(
      setPopupAlert({
        type: Statuses.ERROR,
        message: t('yourApplication.geolocationError'),
        time: 3000,
      }),
    );
  };

  const onSubmit = () => {
    sendGtmEvent(GtmEventEnum.LoanApplication_continueButtonPressed);
    handleGetLocation({ onSuccess: onSuccessGeo, onError: onErrorGeo, onNavigatorError });
  };

  const onChangeRfc = (inputValue: string) => {
    const currentValue = inputValue.replace(REGEXP.withoutLatinNumber, '').toUpperCase();
    let value = rfcInitial;

    if (currentValue.length < 10) {
      value = rfcInitial;
    }
    if (currentValue.startsWith(rfcInitial)) {
      value = currentValue;
    }

    // form.setFieldValue('rfc', currentValue.length < 10 ? rfcCurrent : currentValue);
    form.setFields([
      {
        name: rfc,
        value,
        errors: [],
      },
    ]);
  };

  const onChangeEmail = (inputValue: string) => {
    const currentValue = inputValue.replace(REGEXP.emailEnter, '');

    sendGtmEvent(GtmEventEnum.ID_emailStartedEnter);

    form.setFields([
      {
        name: email,
        value: currentValue,
        errors: [],
      },
    ]);
  };

  const onChangeOccupation = (name: keyof IdFormData, value: PickerDate | PickerValue) => {
    form.setFields([
      {
        name,
        value,
        errors: [],
      },
    ]);
  };

  useEffect(() => {
    dispatch(
      setLoanReviewInputs({
        [REVIEW_INPUT_NAMES.rfc]: rfcCurrent,
        [REVIEW_INPUT_NAMES.email]: emailCurrent,
        [REVIEW_INPUT_NAMES.occupation]: occupationCurrent,
      }),
    );
  }, [dispatch, emailCurrent, occupationCurrent, rfcCurrent]);

  return {
    names: { rfc, email, occupation },
    onChangeEmail,
    onChangeRfc,
    onClickBack,
    showOccupation,
    setShowOccupation,
    onSubmit,
    watchers: { occupationCurrent, rfcCurrent, emailCurrent },
    isLoading,
    isLoadingGeo,
    RfcPlaceholder,
    confirmData,
    isSuccess,
    reset,
    onChangeOccupation,
  };
};
